import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { getApiCall } from "../../../service/httpCall";
import Loader from "../../../components/partials/components/loader";
import '@fortawesome/fontawesome-free/css/all.min.css';
import "../../../assets/custom/css/custom.css";
import Form from "react-bootstrap/Form";
import { USER_ROLES } from "../../../utilities/constant";
import { Storage } from "../../../utilities/setting";
import { toast } from "react-toastify";
import moment from 'moment';

import { CDR_REPORT } from '../../../service/url';
import {
  loadPortalReportData,
  PORTAL_REPORT_RESOURCES,
} from "../../../utilities/portalReportRefresh";

export const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const isoString = dateString.replace(" ", "T") + "Z";
  const date = new Date(isoString);
  const istString = date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour12: false,
  });
  const [datePart, timePart] = istString.split(", ");
  const [day, month, year] = datePart.split("/");
  return `${year}-${month}-${day} ${timePart}`;
};
 
const getDefaultDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date();
  return { start, end };
};

const dateToInputStr = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};
 
const callTypeBadge = (rowData) => {
  const type = rowData.call_type;
  const isIncoming = type === "incoming";
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        backgroundColor: isIncoming ? "#d1f5e0" : "#fde8d8",
        color: isIncoming ? "#1a7a3c" : "#b84a00",
      }}
    >
      {isIncoming ? "Incoming" : "Outgoing"}
    </span>
  );
};
 
const CdrReport = () => {
  const [cdrData, setCdrData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { start, end } = getDefaultDates();
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [didInput, setDidInput] = useState('');
  const [didError, setDidError] = useState('');
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
 
  useEffect(() => {
    const fetchUserData = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user) {
          setLoggedInUserId(user?.id);
          setLoggedInUserRole(user?.role);
        }
      } catch (e) {
        console.error("Invalid JSON in Storage user:", e);
      }
    };
    fetchUserData();
  });
 
  const validateDid = (value) => {
    if (!value || value.trim() === '') {
      setDidError('DID is required.');
      return false;
    }
    if (!/^\d{10,15}$/.test(value.trim())) {
      setDidError('Invalid DID. Must be numeric, 10–15 digits.');
      return false;
    }
    setDidError('');
    return true;
  };
 
  const fetchCdrReport = async (did, start, end, force = false) => {
    if (!validateDid(did)) return;
    if (!loggedInUserId || !loggedInUserRole) {
      return;
    }

    setLoading(true);
    const startParam = start || moment().startOf("month").format("YYYY-MM-DD");
    const endParam   = end   || moment().format("YYYY-MM-DD");
    const didValue = did.trim();

    try {
      const result = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.CDR_REPORT,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { did: didValue, startDate: startParam, endDate: endParam },
        force,
        onBlocked: (remainingMs) => {
          toast.info(
            `CDR report can be refreshed after ${Math.ceil(remainingMs / 1000)} seconds.`
          );
        },
        fetchFn: () =>
          getApiCall(
            `${CDR_REPORT}?did=${didValue}&startDate=${startParam}&endDate=${endParam}`
          ),
      });
      const list = Array.isArray(result.data)
        ? result.data
        : (result.data?.data || []);
      setCdrData(list.map((val, idx) => ({ ...val, key: idx + 1 })));
    } catch (error) {
      toast.error(error?.message || JSON.stringify(error) || "Failed to fetch CDR report");
    } finally {
      setLoading(false);
    }
  };
 
  const handleSearch = () => {
    const start = startDate ? moment(startDate).format("YYYY-MM-DD") : null;
    const end   = endDate   ? moment(endDate).format("YYYY-MM-DD")   : null;
    fetchCdrReport(didInput, start, end, true);
  };
 
  const handleReset = () => {
    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
    setDidInput('');
    setDidError('');
    setCdrData([]);
  };
 
  const handleExportCsv = () => {
    if (!cdrData.length) return;
 
    const headers = [
      "Call Type", "User Id", "Username", "Caller Number", "DID",
      "Start Time", "End Time", "Billing Duration",
      "DTMF", "Trace ID", "IVR Name", "Call Group Name",
      "Routing Type", "Routing ID",
    ];
 
    const rows = cdrData.map((row) => [
      row.call_type       ?? '',
      row.user_id         ?? '',
      row.username        ?? '',
      row.caller_number   ?? '',
      row.did             ?? '',
      formatDateTime(row.start_time),
      formatDateTime(row.end_time),
      row.billing_duration ?? '',
      row.dtmf            ?? '',
      row.trace_id        ?? '',
      row.ivr_name        ?? '',
      row.call_group_name ?? '',
      row.routing_type    ?? '',
      row.routing_id      ?? '',
    ]);
 
    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
 
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href  = url;
    link.setAttribute("download", `CDR_Report_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
 
  if (loading) return <Loader />;
 
  return (
    <div>
      <div className="card" style={{ marginTop: "20px" }}>
 
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Call Detail Report</h4>
          {cdrData.length > 0 && loggedInUserRole !== USER_ROLES.CALL_CENTRE_AGENT && (
            <div className="d-flex">
              <Button
                label="Export to CSV"
                icon="pi pi-download"
                onClick={handleExportCsv}
                className="p-button-active p-button-sm"
                style={{ marginBottom: "10px", marginLeft: "10px" }}
              />
            </div>
          )}
        </div>
 
        <div className="card-body">
 
          <div
            className="d-flex align-items-start flex-wrap mb-3 gap-2"
            style={{ marginLeft: "10px" }}
          >

            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                type="text"
                className={`form-control ${didError ? "is-invalid" : ""}`}
                placeholder="DID *"
                value={didInput}
                onChange={(e) => {
                  setDidInput(e.target.value);
                  if (didError) validateDid(e.target.value);
                }}
                style={{ width: "160px" }}
              />
              {didError && (
                <small className="text-danger" style={{ marginTop: "4px" }}>
                  {didError}
                </small>
              )}
            </div>
 
           
            <Form.Control
              type="date"
              value={dateToInputStr(startDate)}
              max={dateToInputStr(new Date())}
              min={dateToInputStr(moment().subtract(6, "months").toDate())}
              placeholder="Start Date"
              onChange={(e) => setStartDate(inputStrToLocalDate(e.target.value))}
            />

            <span className="mx-1" style={{ lineHeight: "2.4rem" }}>to</span>


            <Form.Control
              type="date"
              value={dateToInputStr(endDate)}
              min={dateToInputStr(startDate)}
              max={dateToInputStr(new Date())}
              placeholder="End Date"
              onChange={(e) => setEndDate(inputStrToLocalDate(e.target.value))}
            />
 
            <Button
              label="Search"
              onClick={handleSearch}
              className="p-button-sm p-button-primary"
            />
            <Button
              label="Clear"
              onClick={handleReset}
              className="p-button-sm p-button-secondary"
            />
          </div>
 
         
          <div className="scroll-container">
            <DataTable
              value={cdrData}
              paginator
              rows={10}
              tableStyle={{ minWidth: "70rem" }}
              emptyMessage={
                didInput
                  ? "No records found."
                  : "Enter a DID and press Search to load records."
              }
            >
              <Column
                field="call_type"
                header="Call Type"
                body={callTypeBadge}
              />
              <Column field="user_id"          header="User Id"         />
              <Column field="username"          header="Username"        />
              <Column field="caller_number"     header="Caller Number"   />
              <Column field="did"               header="DID"             />
              <Column
                field="start_time"
                header="Start Time"
                body={(rowData) => formatDateTime(rowData.start_time)}
              />
              <Column
                field="end_time"
                header="End Time"
                body={(rowData) => formatDateTime(rowData.end_time)}
              />
              <Column field="billing_duration"  header="Billing Duration" />
              <Column field="dtmf"              header="DTMF"            />
              <Column field="trace_id"          header="Trace ID"        />
              <Column field="ivr_name"          header="IVR Name"        />
              <Column field="call_group_name"   header="Call Group Name" />
              <Column field="routing_type"      header="Routing Type"    />
              <Column field="routing_id"        header="Routing ID"      />
            </DataTable>
          </div>
 
        </div>
      </div>
    </div>
  );
};
 
export default CdrReport;